import React from "react";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="bg-[#FFF8E7]" data-testid="page-privacy">
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]">
        <div className="absolute inset-0 halftone-blue opacity-[0.07]" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="inline-block bg-[#1A5DFF] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">Legal</div>
          <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Privacy <span className="text-[#FF2E63] [text-shadow:3px_3px_0_#0A0A0A]">policy.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#0A0A0A] leading-relaxed">
            We collect as little as possible, never sell anything to anyone, and tell you the truth about what we do hold.
          </p>
          <p className="mt-3 text-sm text-[#2C2C2C]">Last updated: 23 May 2026</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 space-y-10">
        <Block title="1. Who we are">
          <p>
            <strong>Divergent Cinema CIC</strong> is a UK-registered Community Interest Company and the data controller for this website.
          </p>
          <p>Contact: <a className="underline" href="mailto:create@divergentcinema.co.uk">create@divergentcinema.co.uk</a></p>
        </Block>

        <Block title="2. What we collect, and why">
          <List items={[
            <span><strong>Google sign-in details</strong> (name, email address, profile picture) — when you sign in to access the Network directory, the Admin area, or to express interest in a production. Used only to authenticate you and link your contributions to your account.</span>,
            <span><strong>Contact form submissions</strong> (name, email, subject, message) — when you write to us via the Contact page. Used only to reply to your enquiry.</span>,
            <span><strong>Network-directory submissions</strong> (the profile data you enter on the Join the Network form: name, role, location, bio, neurodivergence tags, skills, optional email/website, plus an internal email for our records). Published on the directory only after approval by our team.</span>,
            <span><strong>Film submissions</strong> (title, director, YouTube link, year, duration, category, your submitter name and email). Published on the Films page only after approval.</span>,
            <span><strong>Production interest submissions</strong> (the role you offer, optional message; your name and email come from your signed-in Google account). Used to put the project lead in touch with you.</span>,
            <span><strong>A session cookie</strong> (`session_token`) — see the <Link to="/cookies" className="underline">Cookies page</Link>.</span>,
          ]} />
        </Block>

        <Block title="3. Lawful basis">
          <p>We rely on the following lawful bases under the UK GDPR:</p>
          <List items={[
            <span><strong>Consent</strong> — when you submit a contact form, sign up to the directory, submit a film, or express interest in a production.</span>,
            <span><strong>Legitimate interest</strong> — to operate the CIC&apos;s mission (programming, network-building, festival admin) and respond to enquiries.</span>,
            <span><strong>Contract</strong> — where applicable, for paid memberships once payment processing is enabled.</span>,
          ]} />
        </Block>

        <Block title="4. Who we share data with">
          <List items={[
            <span><strong>Google</strong> — when you choose to sign in with Google. Google&apos;s own <a className="underline" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a> applies to that flow.</span>,
            <span><strong>YouTube</strong> — videos on the Films page are embedded from YouTube. We use YouTube&apos;s privacy-enhanced mode, which means YouTube only loads its cookies and tracking after you press play on a video. YouTube&apos;s <a className="underline" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a> applies.</span>,
            <span><strong>Our hosting platform (Emergent)</strong> — to run the site. Data is stored in a database on infrastructure provided by Emergent.</span>,
            <span>We do <strong>not</strong> share data with advertisers, marketing platforms, or data brokers. We do not run advertising or web analytics.</span>,
          ]} />
        </Block>

        <Block title="5. How long we keep it">
          <List items={[
            <span><strong>Contact messages</strong> — kept until the issue is resolved, then deleted within 12 months unless we&apos;re in an ongoing conversation.</span>,
            <span><strong>Network directory entries</strong> — kept while the entry is live on our site. You can request removal at any time.</span>,
            <span><strong>Film submissions</strong> — kept indefinitely if approved (so the film remains on the public page). Rejected submissions deleted within 12 months.</span>,
            <span><strong>Sign-in records</strong> — kept while your account is active. Session cookies expire after 7 days of inactivity.</span>,
          ]} />
        </Block>

        <Block title="6. Your rights">
          <p>Under the UK GDPR you have the right to:</p>
          <List items={[
            "Access the personal data we hold about you",
            "Have inaccurate data corrected",
            "Have your data deleted (the &lsquo;right to be forgotten&rsquo;)",
            "Restrict or object to processing",
            "Receive your data in a portable format",
            "Withdraw consent at any time",
          ]} />
          <p>
            To exercise any of these, email <a className="underline" href="mailto:create@divergentcinema.co.uk">create@divergentcinema.co.uk</a>. We aim to respond within 30 days.
          </p>
        </Block>

        <Block title="7. Complaints">
          <p>
            If you&apos;re unhappy with how we&apos;ve handled your data, you can complain to the UK Information Commissioner&apos;s Office:
            {" "}<a className="underline" href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer">ico.org.uk/make-a-complaint</a>.
          </p>
        </Block>

        <Block title="8. Changes to this policy">
          <p>
            When we change this page we&apos;ll update the &ldquo;Last updated&rdquo; date at the top. For material changes affecting how we use your data, we&apos;ll let you know via the site and (where applicable) by email.
          </p>
        </Block>

        <div className="border-t-2 border-[#0A0A0A] pt-8 flex flex-wrap gap-3">
          <Link to="/cookies" className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] rounded-none px-5 py-3 text-sm font-medium" data-testid="privacy-cookies-link">Cookie policy</Link>
          <Link to="/contact" className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-5 py-3 text-sm font-medium" data-testid="privacy-contact-link">Contact us</Link>
        </div>
      </section>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h2 className="font-heading font-black text-[#0A0A0A] text-2xl tracking-tight mb-3">{title}</h2>
      <div className="space-y-3 text-[#0A0A0A] leading-relaxed">{children}</div>
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="list-none pl-0 space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span className="text-[#FF2E63] font-black mt-1">·</span>
          <span className="flex-1">{item}</span>
        </li>
      ))}
    </ul>
  );
}
